import React from 'react';
import {
  SwipeableDrawer,
  Box,
  Typography,
  Switch,
  Button,
  ButtonBase,
  alpha,
  useTheme,
} from '@mui/material';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LogoutIcon from '@mui/icons-material/ExitToApp';
import TuneIcon from '@mui/icons-material/Tune';
import LockIcon from '@mui/icons-material/LockOutlined';
import AboutIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSignOut } from '../auth/useSignOut';
import { userHasPassword } from '../contexts/userHasPassword';
import { useThemeMode } from '../contexts/ThemeContext';
import { PersonAvatar } from '../components/shared/PersonAvatar';
import { SECURITY_SECTION_ID } from '../components/settings/sections';
import { ABOUT_COPY } from '../components/about/copy';
import { useMyAllocations } from '../hooks/useMyAllocations';
import { FOCUS_RING, radius, shadow } from '../theme/tokens';

const MAX_ALLOCATION_PROJECTS = 4;

const TITLE_ID = 'mobile-account-sheet-title';
const MIN_TAP_TARGET = 44;

/** "OWNER" → "Owner", "TEAM_LEAD" → "Team Lead". */
function titleCaseRole(role: string): string {
  return role
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * One full-width row of the sheet: a ≥ 44px tap target whose whole surface is
 * the control. `trailing` sits at the far end (the dark-mode row's Switch).
 */
function SheetRow({
  icon,
  label,
  onClick,
  ariaLabel = label,
  ariaPressed,
  trailing,
}: {
  icon: React.ReactElement;
  label: string;
  onClick: () => void;
  ariaLabel?: string;
  ariaPressed?: boolean;
  trailing?: React.ReactNode;
}) {
  const theme = useTheme();
  return (
    <ButtonBase
      onClick={onClick}
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
      sx={{
        width: '100%',
        minHeight: MIN_TAP_TARGET,
        px: 1.5,
        py: 1,
        mb: 1,
        borderRadius: `${radius.lg}px`,
        border: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        '& > .MuiSvgIcon-root': { fontSize: 18, color: 'text.secondary' },
        '&:focus-visible': {
          outline: 'none',
          boxShadow: FOCUS_RING(theme.palette.primary.main),
        },
      }}
    >
      {icon}
      <Typography sx={{ flex: 1, textAlign: 'left', fontSize: 13, fontWeight: 600, color: 'text.primary' }}>
        {label}
      </Typography>
      {trailing}
    </ButtonBase>
  );
}

interface MobileAccountSheetProps {
  open: boolean;
  onClose: () => void;
}

/**
 * The "Me" tab's account sheet (D6): a bottom-sheet showing the signed-in user
 * (avatar, job title / role, department), a dark-mode toggle, and Logout. No new
 * route and no new page — it reuses the EXISTING sign-out action
 * (`useSignOut`, the same one the TopBar account menu raises) and the
 * shell theme toggle (`useThemeMode().toggleTheme`). `SwipeableDrawer` bottom
 * sheet (grab handle, safe-area inset, drag-to-dismiss).
 */
const MobileAccountSheet: React.FC<MobileAccountSheetProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const signOut = useSignOut();
  const { isDarkMode, toggleTheme } = useThemeMode();
  const sheetShadow = isDarkMode ? shadow.dark.pop : shadow.light.pop;
  // Only fetch the allocations the sheet actually shows (i.e. while open).
  const { groups: allocationGroups } = useMyAllocations(open);

  const fullName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || '';

  // The human-facing job title leads (when the user is linked to an employee
  // record); the system access role is shown separately as a muted chip rather
  // than conflated with the job title.
  const roleLabel = user?.role ? titleCaseRole(user.role) : null;

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen
      swipeAreaWidth={0}
      slotProps={{
        paper: {
          'aria-labelledby': TITLE_ID,
          sx: {
            borderTopLeftRadius: `${radius.lg}px`,
            borderTopRightRadius: `${radius.lg}px`,
            boxShadow: sheetShadow,
            maxHeight: '88vh',
            overflowY: 'auto',
            px: 2,
            pt: 0.75,
            pb: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
          },
        },
      }}
    >
      {/* Grab handle */}
      <Box
        aria-hidden
        sx={{
          width: 36,
          height: 4,
          borderRadius: '4px',
          backgroundColor: alpha(theme.palette.text.primary, isDarkMode ? 0.28 : 0.18),
          mx: 'auto',
          mt: 0.5,
          mb: 1.5,
        }}
      />

      {/* Identity */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
        <PersonAvatar
          name={fullName}
          src={user?.avatarUrl}
          size={MIN_TAP_TARGET}
          fontSize="0.95rem"
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            id={TITLE_ID}
            sx={{
              fontSize: 14,
              fontWeight: 700,
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {fullName}
          </Typography>
          {user?.jobTitle && (
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{user.jobTitle}</Typography>
          )}
          {user?.department && (
            <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>
              {user.department}
            </Typography>
          )}
        </Box>
        {roleLabel && (
          <Box
            component="span"
            aria-label={`Role: ${roleLabel}`}
            sx={{
              flexShrink: 0,
              mt: '2px',
              px: 1,
              py: 0.25,
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 600,
              color: 'text.secondary',
              backgroundColor: alpha(theme.palette.text.primary, isDarkMode ? 0.1 : 0.06),
            }}
          >
            {roleLabel}
          </Box>
        )}
      </Box>

      {/* This week's allocations — grouped by project (own-data; hidden when
          none / unlinked). One project with one window collapses to a single
          line; recurring windows list their date ranges beneath the project. */}
      {allocationGroups.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'text.secondary',
              mb: 0.5,
            }}
          >
            This week
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {allocationGroups.slice(0, MAX_ALLOCATION_PROJECTS).map((g) => (
              <Box key={g.projectId} sx={{ display: 'flex', gap: 1 }}>
                <Box
                  sx={{
                    width: 3,
                    alignSelf: 'stretch',
                    borderRadius: '2px',
                    backgroundColor: g.color ?? theme.palette.primary.main,
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: 'text.primary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {g.projectName}
                  </Typography>
                  {g.companyName && (
                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{g.companyName}</Typography>
                  )}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.25 }}>
                    {g.windows.map((w) => (
                      <Box key={w.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ flex: 1, minWidth: 0, fontSize: 11, color: 'text.disabled' }}>
                          {w.label}
                        </Typography>
                        <Typography
                          sx={{
                            flexShrink: 0,
                            fontSize: 11,
                            fontWeight: 700,
                            color: 'primary.main',
                            backgroundColor: alpha(theme.palette.primary.main, 0.12),
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 999,
                          }}
                        >
                          {w.percentage}%
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            ))}
            {allocationGroups.length > MAX_ALLOCATION_PROJECTS && (
              <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.25 }}>
                +{allocationGroups.length - MAX_ALLOCATION_PROJECTS} more
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Settings — the mobile twin of the TopBar account-menu entry, so personal
          preferences are reachable on a phone too. The sheet closes before
          navigating so it does not sit over the page it just opened. */}
      <SheetRow
        icon={<TuneIcon />}
        label="Settings"
        onClick={() => {
          onClose();
          navigate('/settings');
        }}
      />

      {/* Change password — the mobile twin of the TopBar entry, on the same
          terms: only a local account has a password Teamlane holds, so an SSO
          user gets no row rather than a dead one. */}
      {userHasPassword(user) && (
        <SheetRow
          icon={<LockIcon />}
          label="Change password"
          onClick={() => {
            onClose();
            navigate(`/settings#${SECURITY_SECTION_ID}`);
          }}
        />
      )}

      {/* Dark-mode toggle — the whole row is the control; the Switch is
          presentational. Reuses the shell theme toggle. */}
      <SheetRow
        icon={<DarkModeIcon />}
        label="Dark mode"
        ariaLabel={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        ariaPressed={isDarkMode}
        onClick={toggleTheme}
        trailing={
          <Switch
            edge="end"
            checked={isDarkMode}
            tabIndex={-1}
            aria-hidden
            disableRipple
            sx={{ pointerEvents: 'none' }}
          />
        }
      />

      <SheetRow
        icon={<AboutIcon />}
        label={ABOUT_COPY.sheetRow}
        ariaLabel={ABOUT_COPY.menuItem}
        onClick={() => {
          onClose();
          navigate('/about');
        }}
      />

      {/* Logout — the existing logout action (token clear → redirect to sign-in). */}
      <Button
        fullWidth
        variant="outlined"
        startIcon={<LogoutIcon />}
        onClick={signOut}
        sx={{ minHeight: MIN_TAP_TARGET, textTransform: 'none', fontWeight: 700, borderRadius: `${radius.lg}px` }}
      >
        Logout
      </Button>
    </SwipeableDrawer>
  );
};

export default MobileAccountSheet;
